import { useState } from "react";

import { api } from "../api";
import { CODE_LENGTH, normaliseCode } from "../linkCode";
import { useAction, useLoad } from "../useLoad";

interface Props {
  teamId: string;
  revision: number;
  onError: (message: string) => void;
}

/**
 * Task 8.5 — pairing a television, and the list of paired ones.
 *
 * This is the other half of design §2.2: the TV shows a six-character code, someone types it here, and
 * the television collects its device token by polling. Nothing sensitive passes through this screen —
 * the token goes to the television, never to the phone.
 */

export function Televisions({ teamId, revision, onError }: Props) {
  const { data, loading, reload } = useLoad(() => api.devices(teamId), [teamId, revision], onError);
  const { busy, run } = useAction(onError);
  const [code, setCode] = useState("");
  const [paired, setPaired] = useState<string | null>(null);

  const devices = data?.devices ?? [];
  const complete = code.length === CODE_LENGTH;

  const redeem = () => {
    if (!complete) return;
    void run(
      async () => {
        await api.redeemCode(teamId, code);
      },
      () => {
        setPaired(code);
        setCode("");
        reload();
      },
    );
  };

  return (
    <>
      <div className="card">
        <p className="section-title">Pair a television</p>
        <p className="muted" style={{ marginBottom: 10 }}>
          Open <strong>Pair with your team</strong> on the Apple TV and type the code it shows.
        </p>
        <div className="field">
          <input
            className="code-input"
            type="text"
            value={code}
            placeholder="······"
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="go"
            aria-label="Link code"
            onChange={(event) => setCode(normaliseCode(event.target.value))}
            onKeyDown={(event) => event.key === "Enter" && redeem()}
          />
        </div>
        <button
          className="btn primary wide"
          style={{ marginTop: 10 }}
          disabled={busy || !complete}
          onClick={redeem}
        >
          {busy ? "Pairing…" : "Pair"}
        </button>
        {paired && (
          <div className="banner info" style={{ marginTop: 10 }}>
            Paired. The television should show your team name within a couple of seconds.
          </div>
        )}
        <p className="muted" style={{ marginTop: 8 }}>
          Codes last five minutes and work once.
        </p>
      </div>

      <div className="card">
        <p className="section-title">
          {devices.length === 1 ? "1 television" : `${devices.length} televisions`}
          {loading ? " · refreshing" : ""}
        </p>
        {devices.length === 0 && !loading && (
          <p className="muted">No televisions paired yet.</p>
        )}
        <ul className="list">
          {devices.map((device) => (
            <li key={device.deviceId} className="row">
              <span className="grow">
                <span className="name">{device.deviceName ?? "Apple TV"}</span>
                {device.pairedAt && (
                  <div className="muted">Paired {new Date(device.pairedAt).toLocaleDateString()}</div>
                )}
              </span>
              {/*
                Requirement 5.6 — revoking takes effect immediately. The credential item is deleted, and
                the authorizer looks the token up by its hash on every request, so there is no cache and
                no window. The television returns to its pairing screen on its next call.
              */}
              <button
                className="btn small danger"
                disabled={busy}
                onClick={() => void run(() => api.revokeDevice(teamId, device.deviceId), reload)}
              >
                Unpair
              </button>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
